import { useEffect, useMemo } from "react";

import * as CHQ from "chq";

import { useDepartments } from "./DepartmentsContext";
import { useLocations } from "./LocationsContext";
import { usePrograms } from "./ProgramsContext";
import { useDegrees } from "./ContextDegrees";
import { useClassYears } from "./ClassYearsContext";
import { useCompanies } from "./CompanyContext";

type Entities = { [key: number]: CHQ.Department }
  | { [key: number]: CHQ.Location }
  | { [key: number]: CHQ.Company }
  | { [key: number]: CHQ.Company };
type SelectOption = { label: string; value: string };

const getSelectOptions = (entities: Entities) => (
  Object.keys(entities)
    .reduce(
      (accum, entityId) => {
        // Not adding parseInt here because it shouldn't be necessary. The
        // result of `Object.keys` will give us what we want here.
        const { name } = entities[entityId as unknown as number];
        return [...accum, { label: name, value: name }];
      },
      [] as SelectOption[]
    )
    .sort((a, b) => a.value.localeCompare(b.value))
);

const useUserSelectOptions = () => {
  const { classYears, onAllClassYearsRequested } = useClassYears();
  const { departments, onAllDepartmentsRequested } = useDepartments();
  const { companies, onAllCompaniesRequested } = useCompanies();
  const { locations, onAllLocationsRequested } = useLocations();
  const { programs, onAllProgramsRequested } = usePrograms();
  const { degrees, onAllDegreesRequested } = useDegrees();

  useEffect(
    () => {
      onAllClassYearsRequested();
      onAllDepartmentsRequested();
      onAllLocationsRequested();
      onAllProgramsRequested();
      onAllDegreesRequested();
      onAllCompaniesRequested();
    },
    [onAllClassYearsRequested, onAllDepartmentsRequested, onAllLocationsRequested,
      onAllProgramsRequested, onAllDegreesRequested, onAllCompaniesRequested]
  );

  return {
    classYearOptions: useMemo(
      () => getSelectOptions(classYears),
      [classYears]
    ),
    companyOptions: useMemo(
      () => getSelectOptions(companies),
      [companies]
    ),
    departmentOptions: useMemo(
      () => getSelectOptions(departments),
      [departments]
    ),
    locationOptions: useMemo(
      () => getSelectOptions(locations),
      [locations]
    ),
    programOptions: useMemo(
      () => getSelectOptions(programs),
      [programs]
    ),
    degreeOptions: useMemo(
      () => getSelectOptions(degrees),
      [degrees]
    )
  };
};

export default useUserSelectOptions;
